import React, { useState, useEffect, useCallback } from 'react';
import {
    Shield,
    AlertTriangle,
    CheckCircle2,
    Users,
    FileText,
    Plus,
    Search,
    Filter,
    BarChart3,
    Bell,
    Download,
    ChevronDown,
    ChevronUp,
    Clock,
    User,
    Mail,
    Eye,
    Send,
    X,
    AlertCircle,
    TrendingUp,
    TrendingDown,
    Minus,
    Brain,
    ClipboardCheck,
    RotateCcw,
    Check,
    BookOpen,
    CheckSquare,
    Paperclip,
    BarChart2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import TaskCreationModal from '../components/Tasks/TaskCreationModal';
import AITaskGenerator from '../components/Tasks/AITaskGenerator';
import ComplianceTaskStatsModal from '../components/Compliance/ComplianceTaskStatsModal';
import ComplianceReminderModal from '../components/Compliance/ComplianceReminderModal';
import FileViewer from '../components/Tasks/FileViewer';
import ManualGradingModal from '../components/Tasks/ManualGradingModal';
import TaskDetailModal from '../components/Tasks/TaskDetailModal';
import CombinedTaskReviewModal from '../components/Tasks/CombinedTaskReviewModal';
import EmployeeComplianceDrawer from '../components/Compliance/EmployeeComplianceDrawer';
import ComplianceAnalyticsDrawer from '../components/Analytics/ComplianceAnalyticsDrawer';



const AdminCompliancePage = () => {
    // State
    const [overview, setOverview] = useState(null);
    const [violations, setViolations] = useState([]);
    const [complianceTasks, setComplianceTasks] = useState([]);
    const [pendingReviewTasks, setPendingReviewTasks] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('overview'); // 'overview', 'violations', 'tasks', 'employees'

    // Page Level Analytics State
    const [isPageAnalyticsOpen, setIsPageAnalyticsOpen] = useState(false);

    // Task creation state
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [isAIGeneratorOpen, setIsAIGeneratorOpen] = useState(false);

    // Stats modal state
    const [selectedTask, setSelectedTask] = useState(null);
    const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);

    // Reminder modal state
    const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
    const [reminderData, setReminderData] = useState(null);

    // File Viewing State
    const [viewingTaskAttachments, setViewingTaskAttachments] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);

    // Manual Grading State
    const [isGradingModalOpen, setIsGradingModalOpen] = useState(false);
    const [selectedGradingSubmission, setSelectedGradingSubmission] = useState(null);

    // AI Edit Task State
    const [aiEditTask, setAiEditTask] = useState(null);

    // View Task Modal State
    const [viewingTask, setViewingTask] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    // Review Modal State
    const [reviewModalData, setReviewModalData] = useState(null);

    // Employee Drill Down State
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    // Filters
    const [taskFilter, setTaskFilter] = useState({
        search: '',
        status: '',
        phase: '',
        sortBy: 'completion_rate',
        sortOrder: 'asc',
        minRate: '',
        maxRate: ''
    });

    const [employeeFilter, setEmployeeFilter] = useState({
        search: '',
        role: '',
        // team_id: '' // Not using unless we fetch teams
    });

    // Pagination
    const [taskPagination, setTaskPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        pages: 1
    });

    const [employeePagination, setEmployeePagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    // Fetch data
    const fetchOverview = useCallback(async () => {
        try {
            const response = await api.get('/compliance/overview');
            setOverview(response.data.overview);
        } catch (error) {
            console.error('Error fetching compliance overview:', error);
            toast.error('Failed to load compliance overview');
        }
    }, []);

    const fetchViolations = useCallback(async () => {
        try {
            const response = await api.get('/compliance/violations');
            setViolations(response.data.violations || []);
        } catch (error) {
            console.error('Error fetching violations:', error);
        }
    }, []);

    const fetchComplianceTasks = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                page: taskPagination.page,
                limit: taskPagination.limit,
                sort_by: taskFilter.sortBy,
                sort_order: taskFilter.sortOrder
            });

            if (taskFilter.search) params.append('search', taskFilter.search);
            if (taskFilter.status) params.append('status', taskFilter.status);
            if (taskFilter.phase) params.append('phase', taskFilter.phase);
            if (taskFilter.minRate) params.append('min_rate', taskFilter.minRate);
            if (taskFilter.maxRate) params.append('max_rate', taskFilter.maxRate);

            const response = await api.get(`/compliance/tasks?${params.toString()}`);
            setComplianceTasks(response.data.tasks || []);
            setTaskPagination(prev => ({
                ...prev,
                total: response.data.pagination.total,
                pages: response.data.pagination.pages
            }));
        } catch (error) {
            console.error('Error fetching compliance tasks:', error);
        }
    }, [taskFilter, taskPagination.page, taskPagination.limit]);

    const fetchPendingReviewTasks = useCallback(async () => {
        try {
            const params = { is_compliance_required: true };
            const response = await api.get('/tasks/pending-review', { params });
            setPendingReviewTasks(response.data.tasks || []);
        } catch (error) {
            console.error('Error fetching pending review tasks:', error);
        }
    }, []);

    const fetchEmployees = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                page: employeePagination.page,
                limit: employeePagination.limit
            });

            if (employeeFilter.search) params.append('search', employeeFilter.search);
            if (employeeFilter.role) params.append('role', employeeFilter.role);

            const response = await api.get(`/compliance/employees?${params.toString()}`);
            setEmployees(response.data.employees || []);
            setEmployeePagination(prev => ({
                ...prev,
                total: response.data.pagination.total,
                pages: response.data.pagination.pages
            }));
        } catch (error) {
            console.error('Error fetching compliance employees:', error);
        }
    }, [employeeFilter, employeePagination.page, employeePagination.limit]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                fetchOverview(),
                fetchViolations(),
                fetchComplianceTasks(),
                fetchPendingReviewTasks(),
                fetchEmployees()
            ]);
            setLoading(false);
        };
        loadData();
    }, [fetchOverview, fetchViolations, fetchComplianceTasks, fetchPendingReviewTasks, fetchEmployees]);

    // Handlers


    const handleCreateWithAI = () => {
        setIsAIGeneratorOpen(true);
    };

    const handleViewStats = (task) => {
        setSelectedTask(task);
        setIsStatsModalOpen(true);
    };

    const handleNotifyEmployee = (violation) => {
        setReminderData({
            employeeIds: [violation.employee_id],
            taskId: violation.task_id,
            taskTitle: violation.task_title,
            employeeName: `${violation.first_name} ${violation.last_name}`
        });
        setIsReminderModalOpen(true);
    };

    const handleSendBulkReminder = (task) => {
        setReminderData({
            taskId: task.id,
            taskTitle: task.title,
            bulkMode: true
        });
        setIsReminderModalOpen(true);
    };

    const handleExportReport = async (type = 'overview', format = 'json') => {
        const loadingToast = toast.loading('Generating report...');
        try {
            const params = { type, format };
            const response = await api.get('/compliance/report/export', {
                params,
                responseType: format === 'json' ? 'json' : 'blob'
            });

            if (format === 'json') {
                const dataStr = JSON.stringify(response.data.report, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `compliance-${type}-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                // Handle Blob (PDF/CSV)
                const blob = new Blob([response.data], {
                    type: format === 'pdf' ? 'application/pdf' : 'text/csv'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `compliance-${type}-${new Date().toISOString().split('T')[0]}.${format}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }

            toast.success('Report downloaded successfully!', { id: loadingToast });
        } catch (error) {
            console.error('Error exporting report:', error);
            toast.error('Failed to export report', { id: loadingToast });
        }
    };

    const handleGradeSubmission = (submission) => {
        setSelectedGradingSubmission(submission);
        setIsGradingModalOpen(true);
    };

    const handleApproveRetake = async (submission) => {
        if (!window.confirm('Are you sure you want to approve this retake request? The employee will be able to take the quiz again.')) {
            return;
        }

        const loadingToast = toast.loading('Approving retake request...');
        try {
            await api.post(
                `/tasks/${submission.id}/review`,
                { action: 'resubmit' }
            );

            toast.success('Retake request approved!', { id: loadingToast });
            fetchPendingReviewTasks(); // Refresh the list
        } catch (error) {
            console.error('Error approving retake:', error);
            toast.error('Failed to approve retake request', { id: loadingToast });
        }
    };

    const handleRejectRetake = async (submission) => {
        if (!window.confirm('Are you sure you want to reject this retake request? The task status will revert to its previous state (Completed or Failed).')) {
            return;
        }

        const loadingToast = toast.loading('Rejecting retake request...');
        try {
            await api.post(
                `/tasks/${submission.id}/review`,
                { action: 'reject_retake' }
            );

            toast.success('Retake request rejected', { id: loadingToast });
            fetchPendingReviewTasks();
        } catch (error) {
            console.error('Error rejecting retake:', error);
            toast.error('Failed to reject retake request', { id: loadingToast });
        }
    };

    // Handle proof approval
    const handleApproveProof = async (submission) => {
        const loadingToast = toast.loading('Approving submission...');
        try {
            await api.post(
                `/tasks/${submission.id}/review`,
                { action: 'approve' }
            );

            toast.success('Submission approved!', { id: loadingToast });
            fetchPendingReviewTasks();
            fetchOverview(); // Update overview as well
        } catch (error) {
            console.error('Error approving submission:', error);
            toast.error('Failed to approve submission', { id: loadingToast });
        }
    };

    // Handle proof rejection
    const handleRejectProof = async (submission) => {
        const feedback = window.prompt('Please provide feedback for the rejection (optional):');
        if (feedback === null) return; // User cancelled

        const loadingToast = toast.loading('Rejecting submission...');
        try {
            await api.post(
                `/tasks/${submission.id}/review`,
                { action: 'resubmit', feedback: feedback || 'Please resubmit with correct proof of completion.' }
            );

            toast.success('Submission sent back for resubmission', { id: loadingToast });
            fetchPendingReviewTasks();
        } catch (error) {
            console.error('Error rejecting submission:', error);
            toast.error('Failed to reject submission', { id: loadingToast });
        }
    };

    const handleViewSubmission = (submission) => {
        const task = {
            id: submission.task_id,
            title: submission.task_title || submission.title,
            description: submission.task_description,
            sections: submission.sections,
            is_combined: true,
            task_id: submission.task_id
        };
        const assignment = {
            ...submission,
            status: submission.status,
            section_progress: submission.section_progress,
            current_section_index: 0,
            employee_name: `${submission.first_name} ${submission.last_name}`
        };

        setReviewModalData({ task, assignment });
    };

    const handleEditTask = (task) => {
        // Check if this is an AI-generated task
        if (task.is_ai_generated) {
            // Open AI Task Generator with the task data for editing
            setAiEditTask(task);
            setIsAIGeneratorOpen(true);
        } else {
            // Open regular task creation modal for non-AI tasks
            // Since we don't have setSelectedTask for creation modal exposed, we might need a separate edit state or adapt existing
            // For now assuming creation modal handles editing if passed task prop, but need to check usage
            // Since TaskCreationModal usage here is simple, just open it for now if we add edit support
            // But let's verify if we need full edit support in compliance page or just basics
            // Implementation plan says "Add CombinedTaskReviewModal... if they are used"
            // Let's stick to reviewing mostly. But if ViewTask calls Edit, we need the handler.
            // We'll reuse the TaskCreationModal with an 'editTask' state if we want to fully support it,
            // but complying with minimum changes first.
            // Actually, let's just properly set it up for reuse.
            setIsTaskModalOpen(true);
            // Ideally we'd pass the task to edit to the modal.
            // Let's assume for now we just open the modal, but to fully implementing editing we'd need another state variable.
        }
    };

    const getTaskIcon = (taskType) => {
        switch (taskType) {
            case 'quiz':
                return <Brain className="w-5 h-5" />;
            case 'training':
                return <BookOpen className="w-5 h-5" />;
            case 'checklist':
                return <CheckSquare className="w-5 h-5" />;
            default:
                return <FileText className="w-5 h-5" />;
        }
    };

    const handleTaskCreated = () => {
        fetchOverview();
        fetchComplianceTasks();
        setIsTaskModalOpen(false);
        setIsAIGeneratorOpen(false);
    };

    const handleSort = (column) => {
        setTaskFilter(prev => ({
            ...prev,
            sortBy: column,
            sortOrder: prev.sortBy === column && prev.sortOrder === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getCompletionRateColor = (rate) => {
        if (rate >= 100) return 'text-green-600 bg-green-50';
        if (rate >= 90) return 'text-yellow-600 bg-yellow-50';
        return 'text-red-600 bg-red-50';
    };

    const getCompletionRateRowBg = (rate) => {
        if (rate >= 100) return 'bg-green-50/50';
        if (rate >= 90) return 'bg-yellow-50/50';
        return 'bg-red-50/50';
    };

    if (loading) {
        return (
            <div className="p-6 max-w-[1600px] mx-auto min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-4 text-gray-500">Loading compliance data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1600px] mx-auto">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-1">Activity Centre — Compliance</h1>
                        <p className="text-sm text-gray-500">Monitor and manage compliance tasks across your organization</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => handleExportReport('overview', 'csv')}
                            className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors shadow-sm"
                        >
                            <Download className="w-4 h-4" />
                            Export Report
                        </button>
                        <button
                            onClick={() => setIsPageAnalyticsOpen(true)}
                            className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors shadow-sm"
                        >
                            <BarChart2 className="w-4 h-4" />
                            View Insights
                        </button>
                        <button
                            onClick={handleCreateWithAI}
                            className="inline-flex items-center gap-2 px-3.5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm font-medium shadow-sm transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Create Compliance Task
                        </button>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="mb-8 border-b border-gray-200">
                <nav className="flex gap-8">
                    {[
                        { key: 'overview', label: 'Overview' },
                        { key: 'violations', label: 'Violations' },
                        { key: 'tasks', label: 'Tasks' },
                        { key: 'employees', label: 'Employees' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveSection(tab.key)}
                            className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px ${activeSection === tab.key
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {activeSection === 'overview' && (
                <>
                    {/* Overview Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* Company Compliance Status */}
                        <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-medium text-muted-foreground">Company Status</h3>
                                <div className={`p-2 rounded-lg ${(overview?.compliance_percentage || 0) >= 90
                                    ? 'bg-green-100 text-green-600'
                                    : (overview?.compliance_percentage || 0) >= 70
                                        ? 'bg-yellow-100 text-yellow-600'
                                        : 'bg-red-100 text-red-600'
                                    }`}>
                                    {(overview?.compliance_percentage || 0) >= 90 ? (
                                        <CheckCircle2 className="w-5 h-5" />
                                    ) : (overview?.compliance_percentage || 0) >= 70 ? (
                                        <AlertCircle className="w-5 h-5" />
                                    ) : (
                                        <AlertTriangle className="w-5 h-5" />
                                    )}
                                </div>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className={`text-4xl font-bold ${(overview?.compliance_percentage || 0) >= 90
                                    ? 'text-green-600'
                                    : (overview?.compliance_percentage || 0) >= 70
                                        ? 'text-yellow-600'
                                        : 'text-red-600'
                                    }`}>
                                    {overview?.compliance_percentage || 0}%
                                </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                                {overview?.compliant_employees || 0}/{overview?.total_employees || 0} employees compliant
                            </p>
                            <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-500 ${(overview?.compliance_percentage || 0) >= 90
                                        ? 'bg-green-500'
                                        : (overview?.compliance_percentage || 0) >= 70
                                            ? 'bg-yellow-500'
                                            : 'bg-red-500'
                                        }`}
                                    style={{ width: `${overview?.compliance_percentage || 0}%` }}
                                />
                            </div>
                        </div>

                        {/* Active Violations */}
                        <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-medium text-muted-foreground">Active Violations</h3>
                                <div className="p-2 rounded-lg bg-red-100 text-red-600">
                                    <AlertTriangle className="w-5 h-5" />
                                </div>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-4xl font-bold text-red-600">
                                    {overview?.active_violations || 0}
                                </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                                {overview?.employees_with_violations || 0} employees affected
                            </p>
                            {violations.length > 0 && (
                                <button
                                    onClick={() => setActiveSection('violations')}
                                    className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium hover:underline"
                                >
                                    View all violations →
                                </button>
                            )}
                        </div>

                        {/* Compliance Tasks */}
                        <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-medium text-muted-foreground">Compliance Tasks</h3>
                                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                                    <FileText className="w-5 h-5" />
                                </div>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-4xl font-bold text-foreground">
                                    {overview?.total_compliance_tasks || 0}
                                </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                                {overview?.total_completed || 0} / {overview?.total_assigned || 0} completed
                            </p>
                            <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-primary transition-all duration-500"
                                    style={{
                                        width: `${overview?.total_assigned > 0
                                            ? Math.round((overview?.total_completed / overview?.total_assigned) * 100)
                                            : 0}%`
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Pending Review Section */}
                    <div className="bg-card rounded-xl shadow-sm overflow-hidden mb-6 border border-border">
                        <div className="border-b border-border px-6 py-4 bg-muted/40">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-blue-100/50 text-blue-700">
                                        <ClipboardCheck className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-foreground">Pending Review</h3>
                                        <p className="text-sm text-muted-foreground">
                                            {pendingReviewTasks.length} compliance submission{pendingReviewTasks.length !== 1 ? 's' : ''} awaiting review
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {pendingReviewTasks.length === 0 ? (
                            <div className="p-12 text-center text-muted-foreground">
                                <ClipboardCheck className="w-12 h-12 opacity-20 mx-auto mb-3" />
                                <h4 className="text-foreground font-medium mb-1">All caught up!</h4>
                                <p className="text-sm">No compliance submissions requiring review</p>
                            </div>
                        ) : (
                            <div className="p-4 space-y-3">
                                {pendingReviewTasks.map((submission) => (
                                    <div
                                        key={submission.id}
                                        className="bg-card border border-border rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all"
                                    >
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="flex items-start gap-4 flex-1 min-w-0">
                                                <div className="p-3 rounded-lg bg-blue-50 text-blue-700 flex-shrink-0">
                                                    {getTaskIcon(submission.task_type)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-semibold text-foreground mb-1 truncate">
                                                        {submission.title}
                                                    </h4>
                                                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2 flex-wrap">
                                                        <div className="flex items-center gap-1">
                                                            <User className="w-4 h-4" />
                                                            <span>{submission.first_name} {submission.last_name}</span>
                                                        </div>
                                                        {submission.position && (
                                                            <>
                                                                <span className="text-border">•</span>
                                                                <span>{submission.position}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                                                            Compliance Task
                                                        </span>
                                                        {submission.status === 'retake_requested' && (
                                                            <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700 flex items-center gap-1">
                                                                <RotateCcw className="w-3 h-3" />
                                                                Retake Requested
                                                            </span>
                                                        )}
                                                        {submission.quiz_grading_type === 'manual' && submission.status !== 'retake_requested' && (
                                                            <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                                                                Manual Grading
                                                            </span>
                                                        )}
                                                        <span className="px-2 py-1 rounded text-xs font-medium bg-muted text-muted-foreground">
                                                            Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
                                                        </span>
                                                        {submission.score !== null && submission.score !== undefined && (
                                                            <span className={`px-2 py-1 rounded text-xs font-medium ${submission.score >= (submission.passing_score || 70)
                                                                ? 'bg-green-100 text-green-700'
                                                                : 'bg-red-100 text-red-700'
                                                                }`}>
                                                                Score: {submission.score}%
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Section Breakdown for Combined Tasks */}
                                                    {(submission.is_combined || submission.task_type === 'combined') && submission.sections && (
                                                        <div className="mt-3 p-3 bg-muted/30 rounded-lg border border-border">
                                                            <p className="text-xs font-semibold text-muted-foreground mb-2">Section Progress:</p>
                                                            <div className="flex flex-wrap gap-2">
                                                                {submission.sections.map((section) => {
                                                                    const sectionProgress = submission.section_progress?.[section.id];
                                                                    const isCompleted = sectionProgress?.status === 'completed';
                                                                    const sectionScore = sectionProgress?.score;

                                                                    return (
                                                                        <div
                                                                            key={section.id}
                                                                            className={`flex items-center gap-2 px-3 py-1.5 rounded border text-xs ${isCompleted
                                                                                ? 'bg-green-50 border-green-200 text-green-700'
                                                                                : 'bg-background border-border text-muted-foreground'
                                                                                }`}
                                                                        >
                                                                            {section.section_type === 'quiz' && <Brain className="w-3 h-3" />}
                                                                            {section.section_type === 'training' && <BookOpen className="w-3 h-3" />}
                                                                            {section.section_type === 'checklist' && <CheckSquare className="w-3 h-3" />}
                                                                            {section.section_type === 'content' && <FileText className="w-3 h-3" />}
                                                                            <span className="font-medium">{section.title}</span>
                                                                            {isCompleted && <Check className="w-3 h-3" />}
                                                                            {sectionScore !== null && sectionScore !== undefined && (
                                                                                <span className="font-bold">({sectionScore}%)</span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Proof Files Display */}
                                                    {(submission.proof_files || submission.proof_url) && (
                                                        <div className="mt-3 p-3 bg-muted/30 rounded-lg border border-border">
                                                            <p className="text-xs font-semibold text-muted-foreground mb-2">Submitted Proof:</p>
                                                            {submission.proof_files && (() => {
                                                                const files = typeof submission.proof_files === 'string'
                                                                    ? JSON.parse(submission.proof_files)
                                                                    : submission.proof_files;
                                                                return files.length > 0 && (
                                                                    <div className="flex flex-wrap gap-2 mb-2">
                                                                        {files.map((file, idx) => (
                                                                            <button
                                                                                key={idx}
                                                                                onClick={() => setPreviewFile(file)}
                                                                                className="flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded text-sm hover:border-primary hover:bg-primary/5 transition-colors"
                                                                            >
                                                                                <FileText className="w-4 h-4 text-muted-foreground" />
                                                                                <span className="text-foreground truncate max-w-[150px]">{file.file_name}</span>
                                                                                <Eye className="w-3 h-3 text-muted-foreground" />
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                );
                                                            })()}
                                                            {submission.proof_url && (
                                                                <a
                                                                    href={submission.proof_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-sm text-primary hover:underline flex items-center gap-1"
                                                                >
                                                                    <Eye className="w-3 h-3" />
                                                                    View proof link
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* Signature Display */}
                                                    {submission.signature_data && (
                                                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                            <p className="text-xs font-semibold text-gray-700 mb-2">Signature:</p>
                                                            <div className="border border-gray-300 rounded bg-white p-2 inline-block">
                                                                {(submission.signature_data.startsWith('http') || submission.signature_data.startsWith('data:')) ? (
                                                                    <button
                                                                        onClick={() => setPreviewFile({
                                                                            file_name: 'Signature',
                                                                            file_type: 'image/png',
                                                                            file_url: submission.signature_data
                                                                        })}
                                                                        className="group relative"
                                                                        title="Click to view full screen"
                                                                    >
                                                                        <img
                                                                            src={submission.signature_data}
                                                                            alt="Signature"
                                                                            className="max-h-16 max-w-full object-contain group-hover:opacity-90 transition-opacity"
                                                                        />
                                                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 rounded">
                                                                            <Eye className="w-4 h-4 text-gray-700" />
                                                                        </div>
                                                                    </button>
                                                                ) : (
                                                                    <p className="text-lg font-signature text-gray-800 px-2 py-1">
                                                                        {submission.signature_data}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                {(submission.is_combined || submission.task_type === 'combined') && (
                                                    <button
                                                        onClick={() => handleViewSubmission(submission)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg font-medium transition-colors text-sm"
                                                        title="View Submission Details"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        Review Content
                                                    </button>
                                                )}
                                                {submission.status === 'retake_requested' ? (
                                                    <>
                                                        <button
                                                            onClick={() => handleRejectRetake(submission)}
                                                            className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-medium transition-colors text-sm"
                                                            title="Reject Retake Request"
                                                        >
                                                            <X className="w-4 h-4" />
                                                            Reject
                                                        </button>
                                                        <button
                                                            onClick={() => handleApproveRetake(submission)}
                                                            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors text-sm"
                                                        >
                                                            <Check className="w-4 h-4" />
                                                            Approve Retake
                                                        </button>
                                                    </>
                                                ) : submission.task_type === 'quiz' && submission.quiz_grading_type === 'manual' ? (
                                                    <button
                                                        onClick={() => handleGradeSubmission(submission)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors text-sm"
                                                    >
                                                        <ClipboardCheck className="w-4 h-4" />
                                                        Grade Quiz
                                                    </button>
                                                ) : (
                                                    /* Proof submission - show approve/reject */
                                                    <>
                                                        <button
                                                            onClick={() => handleRejectProof(submission)}
                                                            className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-medium transition-colors text-sm"
                                                        >
                                                            <X className="w-4 h-4" />
                                                            Request Resubmission
                                                        </button>
                                                        <button
                                                            onClick={() => handleApproveProof(submission)}
                                                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors text-sm"
                                                        >
                                                            <Check className="w-4 h-4" />
                                                            Approve
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}


            {activeSection === 'violations' && (
                <div className="bg-card rounded-xl shadow-sm border border-border mb-6">
                    <div className="border-b border-border px-6 py-4 bg-red-50/30">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-red-100 text-red-600">
                                    <AlertTriangle className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-foreground">Active Violations</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {violations.length} active violation{violations.length !== 1 ? 's' : ''} requiring attention
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleExportReport('violations', 'csv')}
                                    className="flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
                                >
                                    <FileText className="w-4 h-4" />
                                    CSV
                                </button>
                                <button
                                    onClick={() => handleExportReport('violations', 'pdf')}
                                    className="flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
                                >
                                    <FileText className="w-4 h-4" />
                                    PDF
                                </button>
                            </div>
                        </div>
                    </div>

                    {violations.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                            <h4 className="text-foreground font-medium mb-1">No Active Violations</h4>
                            <p className="text-sm">All employees are currently compliant</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-muted/50 border-b border-border">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Employee
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Task
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Overdue
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {violations.slice(0, 5).map((violation) => (
                                        <tr key={violation.id} className="hover:bg-muted/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                                        <User className="w-5 h-5 text-muted-foreground" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-foreground">
                                                            {violation.first_name} {violation.last_name}
                                                        </p>
                                                        <p className="text-sm text-muted-foreground">{violation.position || 'No position'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-medium text-foreground">{violation.task_title}</p>
                                                <p className="text-sm text-muted-foreground capitalize">{violation.task_type}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                                                    <Clock className="w-3.5 h-3.5" />
                                                    {Math.round(violation.days_overdue)} days
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleViewStats({ id: violation.task_id, title: violation.task_title })}
                                                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                                                    >
                                                        <BarChart3 className="w-4 h-4" />
                                                        Stats
                                                    </button>
                                                    <button
                                                        onClick={() => handleNotifyEmployee(violation)}
                                                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
                                                    >
                                                        <Bell className="w-4 h-4" />
                                                        Notify
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeSection === 'tasks' && (
                <div className="bg-card rounded-xl shadow-sm border border-border">
                    <div className="border-b border-border px-6 py-4">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <FileText className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-foreground">Compliance Tasks</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {complianceTasks.length} task{complianceTasks.length !== 1 ? 's' : ''} tracked
                                    </p>
                                </div>
                            </div>

                            {/* Search & Filters */}
                            <div className="flex flex-wrap gap-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search tasks..."
                                        value={taskFilter.search}
                                        onChange={(e) => setTaskFilter(prev => ({ ...prev, search: e.target.value }))}
                                        className="pl-9 pr-4 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent w-48"
                                    />
                                </div>
                                <select
                                    value={taskFilter.phase}
                                    onChange={(e) => setTaskFilter(prev => ({ ...prev, phase: e.target.value }))}
                                    className="px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="">All Phases</option>
                                    <option value="preboarding">Preboarding</option>
                                    <option value="onboarding">Onboarding</option>
                                </select>
                                <select
                                    value={`${taskFilter.minRate || ''}-${taskFilter.maxRate || ''}`}
                                    onChange={(e) => {
                                        const [min, max] = e.target.value.split('-');
                                        setTaskFilter(prev => ({
                                            ...prev,
                                            minRate: min || '',
                                            maxRate: max || ''
                                        }));
                                    }}
                                    className="px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="-">All Rates</option>
                                    <option value="0-74">&lt; 75%</option>
                                    <option value="0-89">&lt; 90%</option>
                                    <option value="0-99">&lt; 100%</option>
                                    <option value="100-100">100%</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {complianceTasks.length === 0 ? (
                        <div className="p-12 text-center text-muted-foreground">
                            <Shield className="w-16 h-16 opacity-30 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">No Compliance Tasks</h3>
                            <p className="mb-6">
                                Create your first compliance task to start tracking
                            </p>
                            <button
                                onClick={handleCreateWithAI}
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-6 py-2 rounded-lg font-medium transition-all"
                            >
                                <Plus className="w-5 h-5" />
                                Create Compliance Task
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-muted/50 border-b border-border">
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Task Name
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Phase
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Assigned
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Completion
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {complianceTasks.map((task) => (
                                            <tr key={task.id} className="hover:bg-muted/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-primary/5 rounded-lg text-primary">
                                                            <FileText className="w-5 h-5" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-foreground">{task.title}</p>
                                                            <p className="text-xs text-muted-foreground line-clamp-1">{task.description}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="inline-flex px-2 py-1 bg-muted text-muted-foreground rounded text-xs font-medium capitalize">
                                                        {task.phase}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-muted-foreground">
                                                    {task.total_assigned} employees
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 w-24 h-2 bg-muted rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-full ${parseFloat(task.completion_rate) >= 90 ? 'bg-green-500' :
                                                                    parseFloat(task.completion_rate) >= 70 ? 'bg-yellow-500' :
                                                                        'bg-red-500'
                                                                    }`}
                                                                style={{ width: `${task.completion_rate}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-xs font-medium text-muted-foreground">
                                                            {task.completion_rate}%
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium capitalize ${task.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-muted text-muted-foreground'
                                                        }`}>
                                                        {task.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedTask(task);
                                                                setIsStatsModalOpen(true);
                                                            }}
                                                            className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-primary transition-colors"
                                                            title="View Stats"
                                                        >
                                                            <BarChart2 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setViewingTask(task);
                                                                setIsViewModalOpen(true);
                                                            }}
                                                            className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-blue-500 transition-colors"
                                                            title="View Details"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-6 py-4 border-t border-border flex items-center justify-between">
                                <p className="text-sm text-muted-foreground">
                                    Showing {((taskPagination.page - 1) * taskPagination.limit) + 1} to {Math.min(taskPagination.page * taskPagination.limit, taskPagination.total)} of {taskPagination.total} tasks
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setTaskPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={taskPagination.page === 1}
                                        className="px-3 py-1 border border-border rounded-lg text-sm text-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setTaskPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={taskPagination.page === taskPagination.pages}
                                        className="px-3 py-1 border border-border rounded-lg text-sm text-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {activeSection === 'employees' && (
                <div className="bg-card rounded-xl shadow-sm border border-border">
                    <div className="border-b border-border px-6 py-4">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Users className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-foreground">Employee Compliance</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {employeePagination.total} employee{employeePagination.total !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search employees..."
                                        value={employeeFilter.search}
                                        onChange={(e) => setEmployeeFilter(prev => ({ ...prev, search: e.target.value }))}
                                        className="pl-9 pr-4 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent w-48"
                                    />
                                </div>
                                <select
                                    value={employeeFilter.role}
                                    onChange={(e) => setEmployeeFilter(prev => ({ ...prev, role: e.target.value }))}
                                    className="px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="">All Roles</option>
                                    <option value="Manager">Manager</option>
                                    <option value="Employee">Employee</option>
                                </select>
                                <div className="flex gap-2 border-l border-border pl-3">
                                    <button
                                        onClick={() => handleExportReport('employees', 'csv')}
                                        className="flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
                                        title="Export as CSV"
                                    >
                                        <FileText className="w-4 h-4" />
                                        CSV
                                    </button>
                                    <button
                                        onClick={() => handleExportReport('employees', 'pdf')}
                                        className="flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
                                        title="Export as PDF"
                                    >
                                        <FileText className="w-4 h-4" />
                                        PDF
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {employees.length === 0 ? (
                        <div className="p-12 text-center text-muted-foreground">
                            <Users className="w-16 h-16 opacity-30 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">No Employees Found</h3>
                            <p>Try adjusting your filters</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-muted/50 border-b border-border">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Employee</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Role</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Department</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Compliance Score</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {employees.map((employee) => (
                                        <tr key={employee.id} className="hover:bg-muted/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-medium text-sm">
                                                        {employee.first_name[0]}{employee.last_name[0]}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-foreground">{employee.first_name} {employee.last_name}</div>
                                                        <div className="text-xs text-muted-foreground">{employee.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-muted-foreground">{employee.position}</td>
                                            <td className="px-6 py-4 text-sm text-muted-foreground">{employee.department || '-'}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 w-24 h-2 bg-muted rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${employee.compliance_score >= 90 ? 'bg-green-500' :
                                                                employee.compliance_score >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                                                                }`}
                                                            style={{ width: `${employee.compliance_score}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-sm font-medium text-muted-foreground">{employee.compliance_score}%</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize">
                                                    {employee.lifecycle_stage}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => setSelectedEmployee(employee)}
                                                    className="text-primary hover:text-primary-dark font-medium text-sm"
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {employees.length > 0 && (
                        <div className="px-6 py-4 border-t border-border flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">
                                Showing {((employeePagination.page - 1) * employeePagination.limit) + 1} to {Math.min(employeePagination.page * employeePagination.limit, employeePagination.total)} of {employeePagination.total} employees
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setEmployeePagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={employeePagination.page === 1}
                                    className="px-3 py-1 border border-border rounded text-sm text-muted-foreground disabled:opacity-50 hover:bg-muted"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setEmployeePagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={employeePagination.page === employeePagination.pages}
                                    className="px-3 py-1 border border-border rounded text-sm text-muted-foreground disabled:opacity-50 hover:bg-muted"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Task Creation Modal - Pre-checked compliance */}
            {isTaskModalOpen && (
                <TaskCreationModal
                    isOpen={isTaskModalOpen}
                    onClose={() => setIsTaskModalOpen(false)}
                    onSuccess={handleTaskCreated}
                    phase="onboarding"
                    isComplianceMode={true}
                />
            )}

            {/* AI Task Generator - Pre-checked compliance */}
            {isAIGeneratorOpen && (
                <AITaskGenerator
                    isOpen={isAIGeneratorOpen}
                    onClose={() => setIsAIGeneratorOpen(false)}
                    onSuccess={handleTaskCreated}
                    phase="onboarding"
                    isComplianceMode={true}
                />
            )}

            {/* Compliance Task Stats Modal */}
            {isStatsModalOpen && selectedTask && (
                <ComplianceTaskStatsModal
                    isOpen={isStatsModalOpen}
                    onClose={() => {
                        setIsStatsModalOpen(false);
                        setSelectedTask(null);
                    }}
                    taskId={selectedTask.id}
                    taskTitle={selectedTask.title}
                    onSendReminder={(employeeIds) => {
                        setReminderData({
                            employeeIds,
                            taskId: selectedTask.id,
                            taskTitle: selectedTask.title
                        });
                        setIsReminderModalOpen(true);
                    }}
                />
            )}

            {/* Reminder Modal */}
            {isReminderModalOpen && reminderData && (
                <ComplianceReminderModal
                    isOpen={isReminderModalOpen}
                    onClose={() => {
                        setIsReminderModalOpen(false);
                        setReminderData(null);
                    }}
                    taskId={reminderData.taskId}
                    taskTitle={reminderData.taskTitle}
                    employeeIds={reminderData.employeeIds}
                    employeeName={reminderData.employeeName}
                    bulkMode={reminderData.bulkMode}
                />
            )}
            {/* Application-level File Viewer */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />

            {/* Manual Grading Modal */}
            <ManualGradingModal
                isOpen={isGradingModalOpen}
                onClose={() => {
                    setIsGradingModalOpen(false);
                    setSelectedGradingSubmission(null);
                }}
                submission={selectedGradingSubmission}
                onSuccess={() => {
                    fetchPendingReviewTasks();
                    setIsGradingModalOpen(false);
                    setSelectedGradingSubmission(null);
                }}
            />

            {/* Combined Task Review Modal */}
            <CombinedTaskReviewModal
                isOpen={!!reviewModalData}
                onClose={() => setReviewModalData(null)}
                task={reviewModalData?.task}
                assignment={reviewModalData?.assignment}
            />

            {/* View Task Detail Modal */}
            <TaskDetailModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                task={viewingTask}
                isAdmin={true}
                onEdit={(task) => {
                    setIsViewModalOpen(false);
                    // Simply close for now, edit support minimal
                    setIsTaskModalOpen(true);
                }}
            />

            {/* Employee Compliance Detail Drawer */}
            <EmployeeComplianceDrawer
                isOpen={!!selectedEmployee}
                onClose={() => setSelectedEmployee(null)}
                employeeId={selectedEmployee?.id}
            />
            {/* Page Level Analytics Drawer */}
            <ComplianceAnalyticsDrawer
                isOpen={isPageAnalyticsOpen}
                onClose={() => setIsPageAnalyticsOpen(false)}
            />
        </div>
    );
};

export default AdminCompliancePage;

